import type { ColumnType } from 'antd/es/table/interface';
import type { InvoiceTransactionDetail } from 'ap-openapi';
import { useMemo } from 'react';

import { useGetTransactionProperties } from '@/context/services/invoice_report/TransactionProperties.service';
import {
  getDefaultColumnProps,
  getDynamicColumnProps,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/helpers/invoiceTransactionColumns';
import { TRANSACTION_MASTER_FIELD_NAMES } from '@/features/transaction_items/constants';
import { useColumns } from '../Table/Columns/columns';

export type InvoiceTransactionColumn = ColumnType<
  // `id` is optional in the auto-generated `InvoiceTransactionDetail` type.
  // But it is required for the table, so make it explicitly required for the column type.
  InvoiceTransactionDetail & Required<Pick<InvoiceTransactionDetail, 'id'>>
>;

export const useInvoiceTransactionColumns = (
  reportId: string
): InvoiceTransactionColumn[] => {
  // Fetch fields that will be used to generate dynamic columns
  const { data: dynamicTransactionFields } =
    useGetTransactionProperties(reportId);

  // Default columns are hard-coded
  const defaultColumns = useColumns();

  return useMemo(() => {
    const columns: InvoiceTransactionColumn[] = [];

    // If the array is empty, that means the dynamic columns haven't been defined.
    // If the dynamic columns aren't defined, use the default columns as-is
    // (this will happen if user hasn't visited the config page yet)
    if (dynamicTransactionFields.length === 0) {
      return defaultColumns;
    }

    // Column that exists in defaults, but not in the API response
    const fixedColumn = getDefaultColumnProps(
      defaultColumns,
      TRANSACTION_MASTER_FIELD_NAMES.NUMBER
    );
    if (fixedColumn) {
      columns.push(fixedColumn);
    }

    // Dynamic columns generated by merging API result with default columns
    dynamicTransactionFields.forEach((dynamicFieldProps) => {
      const columnProps = getDynamicColumnProps(
        defaultColumns,
        dynamicFieldProps
      );
      if (columnProps) {
        columns.push(columnProps);
      }
    });

    return columns;
  }, [dynamicTransactionFields, defaultColumns]);
};
